import React, { useEffect, useState } from 'react';
import { Card, EmptyState, ErrorNote } from '../ui';

interface TopPost {
  title: string | null;
  contentType: string;
  pageLabel: string;
  publishedAt: string;
  reach: number | null;
  views: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  engagement: number;
}
interface Summary {
  count: number;
  recommendations: string[];
  topPosts: TopPost[];
}
interface PageOption { id: string; internalLabel: string }

export function AnalyticsScreen({ pageId }: { pageId?: string } = {}) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [localFilter, setLocalFilter] = useState('');
  const pageFilter = pageId ?? localFilter;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setPages((await window.pagepilot.pages.list()) as PageOption[]);
        setSummary((await window.pagepilot.analytics.summary(pageFilter || undefined)) as Summary);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [pageFilter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Performance & Recommendations</h1>
        {!pageId && (
          <select value={localFilter} onChange={(e) => setLocalFilter(e.target.value)} className="ml-auto rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm">
            <option value="">All Pages</option>
            {pages.map((p) => <option key={p.id} value={p.id}>{p.internalLabel}</option>)}
          </select>
        )}
      </div>
      {error && <ErrorNote message={error} />}
      <p className="text-xs text-neutral-500">
        Metrics come only from Facebook's official Insights API for content you published. Values that Facebook does not
        return are shown as "—"; nothing is estimated or fabricated. Recommendations are evidence-based suggestions, not
        guarantees.
      </p>

      {!summary || summary.count === 0 ? (
        <EmptyState
          title="No measured performance yet"
          hint="Once content is published and Facebook returns metrics (usually within an hour), reach, views and engagement appear here."
        />
      ) : (
        <>
          <Card title="Recommendations">
            <ul className="space-y-1.5 text-sm text-neutral-300">
              {summary.recommendations.map((r, i) => (
                <li key={i} className="flex gap-2"><span className="text-brand-300">→</span>{r}</li>
              ))}
            </ul>
          </Card>

          <Card title={`Top posts by engagement (${summary.count} measured)`}>
            <table className="w-full text-left text-xs">
              <thead className="text-neutral-500">
                <tr>
                  <th className="pb-2 pr-3 font-medium">Title</th>
                  <th className="pb-2 pr-3 font-medium">Type</th>
                  <th className="pb-2 pr-3 font-medium">Reach</th>
                  <th className="pb-2 pr-3 font-medium">Views</th>
                  <th className="pb-2 pr-3 font-medium">Reactions</th>
                  <th className="pb-2 pr-3 font-medium">Comments</th>
                  <th className="pb-2 font-medium">Shares</th>
                </tr>
              </thead>
              <tbody>
                {summary.topPosts.map((p, i) => (
                  <tr key={i} className="border-t border-neutral-800/60">
                    <td className="max-w-[260px] truncate py-1.5 pr-3 text-neutral-200">{p.title ?? 'Untitled'}</td>
                    <td className="py-1.5 pr-3">{p.contentType === 'REEL' ? '▣' : '◻'}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{p.reach ?? '—'}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{p.views ?? '—'}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{p.reactions ?? '—'}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{p.comments ?? '—'}</td>
                    <td className="py-1.5 tabular-nums">{p.shares ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
